import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { CoreDialog, CoreDialogActions, CoreTypography } from '@youscience/core';

import { ROUTES } from '@constants/ROUTES';
import ActionButton from '@components/ActionButton';
import useOptInStore from '@store/OptInStore';

import HatIcon from '@images/survey-hat.svg?react';
import FlyingDiscIcon from '@images/flying-disc.svg?react';

import { sxStyles, StyledDialogContent } from './SurveySuccessDialog.styles';

interface SuccessDialogItem {
  icon: ReactNode;
  description: string;
  route: { searching: string; favorites: string };
}

const SUCCESS_DIALOG_DATA: { [key: string]: SuccessDialogItem } = {
  school: {
    icon: <HatIcon />,
    description:
      'We saved your input. Now, you’re one step closer to finding your best-fit education. See or edit your input anytime by clicking “Saved schools.”',
    route: { favorites: ROUTES.FAVORITE_SCHOOLS, searching: ROUTES.SCHOOLS },
  },
  major: {
    icon: <FlyingDiscIcon />,
    description:
      'We saved your program/major notes. You are one step closer to finding your next educational opportunity! You can hit the “Saved Majors” Button to edit or review your notes anytime. ',
    route: { favorites: ROUTES.FAVORITE_MAJORS, searching: ROUTES.MAJORS },
  },
};

interface SuccessDialogProps {
  surveyType: string;
  additionalText?: string;
  isOpenSuccessDialog: boolean;
  onCloseSuccessDialog: () => void;
}

export const SurveySuccessDialog = (props: SuccessDialogProps) => {
  const { surveyType, additionalText, isOpenSuccessDialog, onCloseSuccessDialog } = props;

  const {
    optInConnectionData: { share },
  } = useOptInStore((state) => state);

  return (
    <CoreDialog sx={sxStyles.dialogWrap} open={isOpenSuccessDialog}>
      <StyledDialogContent>
        {SUCCESS_DIALOG_DATA[surveyType].icon}

        <CoreTypography sx={sxStyles.title} variant="h4" component="h3">
          Thank you for your time!
        </CoreTypography>

        <CoreTypography sx={sxStyles.description}>{SUCCESS_DIALOG_DATA[surveyType].description}</CoreTypography>

        {surveyType === 'school' && share && (
          <CoreTypography sx={sxStyles.additionalText}>{additionalText}</CoreTypography>
        )}
      </StyledDialogContent>

      <CoreDialogActions sx={sxStyles.footer}>
        <ActionButton
          size="large"
          variant="outlined"
          data-testid="proceedSearching"
          to={SUCCESS_DIALOG_DATA[surveyType].route.searching}
          component={Link}
          onClick={onCloseSuccessDialog}
        >
          Proceed searching
        </ActionButton>

        <ActionButton
          size="large"
          data-testid="goToFavorites"
          to={SUCCESS_DIALOG_DATA[surveyType].route.favorites}
          component={Link}
          onClick={onCloseSuccessDialog}
        >
          Go to favorites
        </ActionButton>
      </CoreDialogActions>
    </CoreDialog>
  );
};
